import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TokenService, HolderService } from 'services';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useAsync } from 'react-use';
import { convertStringToBN } from 'app/contexts/ClientContext';
import useWeb3ModalWithCallback from 'app/helpers/useWeb3ModalWithCallback';
import constants from '../../../../components/trexConstants';
import { getApproveDvDContent } from './Confirmation';
import {
  DsButton,
  DsInputString,
  DsInputBignumber,
  DsLogo,
  DsEthAvatar,
  DsForm,
  DsTag,
  DsSelectSimple,
  DsExplorerLink,
} from '@tokenyico/design-system/dist/reactJS';
import {
  vMaxBigNumber,
  vWalletAddress,
  vGreaterThanBigNumber,
  vBigNumber,
  dsConfirm,
} from '@tokenyico/design-system';

const DvDTransferToken = ({
  balance,
  setSuccess,
  setError,
  onCloseModal,
  forwardedAmountRef,
  forwardedWalletRef,
}) => {
  const INIT_VALUES = {
    recipient: null,
    quantityToSend: null,
    quantityToReceive: null,
    symbolToReceive: null,
    tokenToReceive: null,
  };
  const token = get(balance, 'token');
  const sender = get(balance, 'holder.details');
  // const conditionalTransfersEnabled = get(balance, 'token.conditionalTransfersEnabled', false);
  const tokenDecimals = get(balance, 'token.decimals', 0);
  const availableBalance = get(balance, 'unfrozenBalance', 0);

  const { t } = useTranslation();
  const history = useHistory();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [investor, setInvestor] = useState(null);
  const [errorSubmit, setErrorSubmit] = useState();
  const [errorWeb3, setErrorWeb3] = useState();
  const [stableCoins, setStableCoins] = useState([]);
  const [values, setValues] = useState(INIT_VALUES);

  const { callContract, onConnectWallet, isWeb3ModalOpen } = useWeb3ModalWithCallback(async () => {
    if (Object.values(values).every((v) => v !== null)) onSubmit(); // if any value is null (empty) don't call submit on wallet connect
  });

  useAsync(async () => {
    try {
      const coins = await TokenService.getStableCoins(token.blockchainNetwork);
      setStableCoins(coins);
    } catch (error) {
      setError(error);
    }
  });

  const handleOnChangeRecipient = async (detail) => {
    setValues({
      ...values,
      recipient: detail,
    });

    setErrorSubmit(null);
    if (!vWalletAddress(detail)) {
      setIsLoading(true);
      const getInvestor = await HolderService.getHolderByWallet(token.id, detail)
        .then(({ investor }) => investor)
        .catch(() => '');

      setInvestor(getInvestor);
      setIsLoading(false);
    } else {
      setInvestor(null);
    }
    return detail;
  };

  const onConfirmSubmit = async () => {
    const params = { values, token, sender, investor };
    if (
      await dsConfirm({
        heading: `${t('shared.createTransferRequest')}?`,
        content: getApproveDvDContent(t, params),
        action: `${t('shared.yes')}, ${t('shared.createTransferRequest').toLocaleLowerCase()}`,
        version: 'success',
      })
    ) {
      onSubmit();
    }
  };

  const handleWeb3 = async (allowanceInput, initDvdInput) => {
    return callContract('increaseAllowance', allowanceInput).then(() =>
      callContract('initiateDVDTransfer', initDvdInput),
    );
  };

  const handleWeb3Error = (res) => {
    const error = res.details || res.message; // res.messages is too long to display.
    console.log(res);
    setErrorWeb3(error);
  };

  const onSubmit = async () => {
    if (sender.provider === 'INTEGRATED') {
      setErrorWeb3(t('validation.walletTypeNotSupported'));
      return;
    }
    setErrorSubmit(null);
    setErrorWeb3(null);
    setIsSubmitting(true);

    try {
      const data = {
        senderToken: token.address,
        senderAmount: values.quantityToSend.toString(),
        counterpartToken: values.tokenToReceive.address,
        counterpartAmount: values.quantityToReceive.toString(),
        counterpart: values.recipient,
      };

      // if wallet provider != onchainid
      // Trigger transfer as current implementation, either for direct or conditional
      // if (conditionalTransfersEnabled) { TODO

      await TokenService.postValidateDvdTransfer(token.id, {
        senderAddress: sender.wallet,
        trexAmount: data.senderAmount,
        counterpartAddress: data.counterpart,
        erc20TokenAddress: data.counterpartToken,
        erc20Amount: data.counterpartAmount,
      });

      if (sender.provider === 'ONCHAINID') {
        return TokenService.postSendDvdTransfer(token.id, {
          senderAddress: sender.wallet,
          trexAmount: data.senderAmount,
          counterpartAddress: data.counterpart,
          erc20TokenAddress: data.counterpartToken,
          erc20Amount: data.counterpartAmount,
        })
          .then(() => {
            setIsSubmitting(false);
            setSuccess(t('shared.transactionBroadcasted'));
            history.push('/requests');
            // handleCloseModal();
          })
          .catch((res) => {
            handleWeb3Error(res);
            setIsSubmitting(false);
          });
      } else {
        const isWalletConnected = onConnectWallet();
        if (!isWalletConnected) {
          setIsSubmitting(false);
          return;
        }
        const allowanceInput = {
          tokenAddress: token.address,
          fromAddress: sender.wallet,
          toAddress: get(
            constants.dvdTransferManagersContracts,
            token.blockchainNetwork.toLowerCase(),
          ), //spender
          amount: convertStringToBN(data.senderAmount, token.decimals),
        };

        const initDvdInput = {
          ...data,
          senderAmount: convertStringToBN(data.senderAmount, token.decimals),
          counterpartAmount: convertStringToBN(
            data.counterpartAmount,
            values.tokenToReceive.decimals,
          ),
        };
        return handleWeb3(allowanceInput, initDvdInput)
          .then(() => {
            setIsSubmitting(false);
            setSuccess(t('shared.transactionBroadcasted'));
            handleCloseModal();
          })
          .catch((res) => {
            handleWeb3Error(res);
            setIsSubmitting(false);
          });
      }
    } catch (e) {
      setIsSubmitting(false);
      setErrorSubmit(e.message);
    }

    setIsSubmitting(false);
  };

  const reset = () => {
    forwardedAmountRef.current.hideError();
    forwardedWalletRef.current.hideError();
    setIsSubmitting(false);
    setErrorSubmit(null);
    setErrorWeb3(null);
    setValues(INIT_VALUES);
    setInvestor(null);
  };

  const handleCloseModal = () => {
    reset();
    onCloseModal();
  };

  return (
    <>
      <DsForm onSubmit={onConfirmSubmit} id="transfer-token">
        <div className="_flex:column _text:sm _p:x(3)">
          <div className="_m:bottom(4) _m:top(3) _p:x(1)">
            <div className="_text(500) _m:bottom(1) _flex:align(center)">
              {t('shared.your')}{' '}
              {sender.provider === 'ONCHAINID' && (
                <DsLogo
                  name="onchainid"
                  style={{
                    width: '5rem',
                    margin: '0 0.3rem',
                  }}
                />
              )}{' '}
              {t('shared.wallet').toLocaleLowerCase()}
            </div>
            <div className="_text:xs _text-grey-900 _m:bottom(2)">
              {t('shared.dvDSenderWalletDesc')}
            </div>
            <DsExplorerLink
              network={token.blockchainNetwork}
              address={sender.wallet}
              type="wallet"
            />
          </div>
          <div className="_m:bottom(4) _m:top(2) _p:x(1)">
            <div className="_text(500) _m:bottom(1)">{t('shared.counterpartInvestor')} *</div>
            <div className="_text:xs _text-grey-900">{t('shared.counterpartAlert1')}</div>
            <div className="_text:xs _text-grey-900 _m:bottom(2)">
              {t('shared.counterpartAlert2')}
            </div>

            <DsInputString
              isFloatLabel={true}
              name="recipient"
              type="text"
              placeholder={
                t('shared.counterpartInvestor') +
                ' ' +
                t('shared.walletAddress').toLocaleLowerCase()
              }
              value={values.recipient}
              ref={forwardedWalletRef}
              onDsInput={({ detail }) => handleOnChangeRecipient(detail)}
              validate={vWalletAddress}
            />

            {investor && !isLoading && (
              <span>
                <span className="_text-grey-900">{t('shared.name')}:</span>
                <span className="_m:left(2)">
                  <DsEthAvatar size="12px" address={values.recipient} /> {investor}
                </span>
              </span>
            )}
          </div>
          <div className="_m:bottom(4) _p:x(1)">
            <div className="_text(500) _m:bottom(1)">{t('shared.tokensToSend')} *</div>
            <div className="_text:xs _text-grey-900 _m:bottom(2)">
              {t('shared.tokensToSendAlert')}
            </div>
            <DsInputBignumber
              symbol={token.symbol}
              isFloatLabel={true}
              name="quantityToSend"
              type="text"
              ref={forwardedAmountRef}
              value={values.quantityToSend}
              placeholder={t('shared.tokenQuantityToSend')}
              onDsInput={({ detail }) =>
                setValues({
                  ...values,
                  quantityToSend: detail,
                })
              }
              validate={(v) =>
                vBigNumber(tokenDecimals)(v) ||
                vGreaterThanBigNumber(0)(v) ||
                vMaxBigNumber(availableBalance)(v)
              }
            />
          </div>
          <div className="_m:bottom(2) _p:x(1)">
            <div className="_text(500) _m:bottom(1)">{t('shared.tokensToReceive')} *</div>
            <div className="_text:xs _text-grey-900">{t('shared.tokensToReceiveAlert')}</div>

            <div className="_grid">
              <div className="_col(12) _col:md(8) _flex:column">
                <DsInputBignumber
                  isFloatLabel={true}
                  name="quantityToReceive"
                  type="text"
                  ref={forwardedAmountRef}
                  value={values.quantityToReceive}
                  placeholder={t('shared.tokenQuantityToReceive')}
                  onDsInput={({ detail }) =>
                    setValues({
                      ...values,
                      quantityToReceive: detail,
                    })
                  }
                  validate={(v) => vBigNumber(2)(v) || vGreaterThanBigNumber(0)(v)}
                  className="_flex:column _m:top(1)"
                />
              </div>
              <div className="_col(12) _col:md(4) _flex:column">
                {stableCoins && stableCoins.length > 0 && (
                  <DsSelectSimple
                    value={values.symbolToReceive}
                    onDsInput={({ detail }) =>
                      setValues({
                        ...values,
                        symbolToReceive: detail,
                        tokenToReceive:
                          stableCoins.find((coin) => coin.symbol === detail) || undefined,
                      })
                    }
                    name="symbolToReceive"
                    validate={() => values.symbolToReceive === null}
                    options={stableCoins.map((coin) => ({
                      label: coin.symbol,
                      value: coin.symbol,
                    }))}
                  />
                )}
              </div>
            </div>
          </div>
          {errorSubmit && (
            <div className="help-error">
              <div className="_align:center">{errorSubmit}</div>
            </div>
          )}
          {errorWeb3 && (
            <div className="help-error">
              <div className="_align:center">{errorWeb3}</div>
            </div>
          )}
          <DsTag
            label={t('shared.dvdTransferAlert')}
            version="info"
            isSquare="true"
            size="sm"
            icon="info"
            iconSize="14px"
            className="_m:bottom(3)"
          />
          <div className="_flex:row _m:top(4) _flex:justify(end)">
            <DsButton
              version="monochrome"
              label={t('shared.cancel')}
              type="button"
              onClick={() => handleCloseModal()}
            />

            <DsButton
              version="primary"
              label={t('shared.createTransferRequest')}
              type="submit"
              className="_m:left(2)"
              icon="checkmark"
              isReverse
              isLoading={isSubmitting || isWeb3ModalOpen}
            />
          </div>
        </div>
      </DsForm>
    </>
  );
};

export default DvDTransferToken;
